// Sync API functions for Cleancy Web App
// Aligned with cleancy mobile app data flow

import { API_VERSION } from "../constants";
import { ReportData, UserContextData } from "../types/global.d";
import { API_URL, fetchWithErrorHandling, getErrorMessage } from "../utils/api";
import {
  normalizeReportsTextFields,
  normalizeReportTextFields,
  normalizeUserDataTextFields,
} from "../utils/textNormalization";

export interface SyncOptions {
  lastSync?: string;
  lat?: number;
  lng?: number;
  page?: number;
  limit?: number;
  minimal?: boolean;
  strategy?: "optimal";
  nearbyLimit?: number;
}

export interface SyncResponse {
  reports?:
    | ReportData[]
    | {
        nearby: ReportData[];
        distant: ReportData[];
        strategy: string;
      };
  isIncremental?: boolean;
  isGeographic?: boolean;
  totalCount?: number;
  currentPage?: number;
  hasMore?: boolean;
  serverTime: string;
  user?: UserContextData;
  minimal?: boolean;
  // v3: true when `reports` is the status-only tier (id/geoposition/status/
  // sortTimestamp only) and should be merged as a pin-status overlay.
  statusOnly?: boolean;
  removedReports?: string[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasMore: boolean;
    limit: number;
  };
}

const normalizeSyncResponseTextFields = (response: SyncResponse): SyncResponse => {
  let reports = response.reports;

  if (Array.isArray(reports)) {
    reports = normalizeReportsTextFields(reports);
  } else if (reports && typeof reports === "object" && "nearby" in reports) {
    reports = {
      ...reports,
      nearby: normalizeReportsTextFields(reports.nearby || []),
      distant: normalizeReportsTextFields(reports.distant || []),
    };
  }

  return {
    ...response,
    reports,
    user: response.user
      ? normalizeUserDataTextFields(response.user)
      : response.user,
  };
};

/**
 * Enhanced sync endpoint that supports incremental updates and pagination
 * For web app, we fetch full data instead of preview/minimal
 */
export const syncData = async (
  options: SyncOptions = {}
): Promise<SyncResponse> => {
  try {
    const params = new URLSearchParams();

    params.append("api_version", API_VERSION);

    if (options.lastSync) {
      params.append("lastSync", options.lastSync);
    }
    if (options.lat !== undefined) {
      params.append("lat", options.lat.toString());
    }
    if (options.lng !== undefined) {
      params.append("lng", options.lng.toString());
    }
    if (options.page) {
      params.append("page", options.page.toString());
    }
    if (options.limit) {
      params.append("limit", options.limit.toString());
    }
    // Note: For web we don't use minimal flag - we want full data
    if (options.strategy) {
      params.append("strategy", options.strategy);
    }
    if (options.nearbyLimit) {
      params.append("nearbyLimit", options.nearbyLimit.toString());
    }

    const url = params.toString()
      ? `${API_URL}/sync?${params}`
      : `${API_URL}/sync`;

    console.log("🔄 API CALL:", url);

    const data = await fetchWithErrorHandling(url);
    return normalizeSyncResponseTextFields(data);
  } catch (error: any) {
    throw new Error(`Failed to sync data: ${getErrorMessage(error)}`);
  }
};

/**
 * Fetch all reports with full data
 * This is the primary method for web app - simpler than mobile
 */
export const fetchAllReports = async (): Promise<SyncResponse> => {
  console.log("🚀 FETCHING ALL REPORTS (full data)");
  return syncData();
};

/**
 * Cold-start map sync (v3): status + coordinates only for ALL reports.
 * With API_VERSION=3 and no minimal/strategy params, the server returns the
 * status-only tier (`_id, geoposition, status, sortTimestamp`, statusOnly:true).
 * Preview data is lazy-loaded per visible list row; full data is loaded on
 * select. Mirrors the mobile app's fetchStatusOnlySync.
 */
export const fetchStatusOnlySync = async (): Promise<SyncResponse> => {
  console.log("🚀 FETCHING STATUS-ONLY MAP DATA");
  return syncData();
};

/**
 * Get single report by ID
 */
export const getReportById = async (reportId: string): Promise<ReportData> => {
  try {
    const data = await fetchWithErrorHandling(`${API_URL}/reports/${reportId}`);
    return normalizeReportTextFields(data);
  } catch (error: any) {
    throw new Error(`Failed to get report: ${getErrorMessage(error)}`);
  }
};

/**
 * Get multiple reports by their IDs in a single API call.
 * `preview: true` asks for the lightweight preview tier (single imagePreview,
 * no full images array / comment / cleaned object) — used by the list
 * lazy-load. Full data (all images, comment, cleaned) is fetched on open.
 */
export const getReportsByIds = async (
  reportIds: string[],
  options: { preview?: boolean } = {}
): Promise<ReportData[]> => {
  try {
    const data = await fetchWithErrorHandling(`${API_URL}/reports/bulk`, {
      method: "POST",
      body: JSON.stringify({
        ids: reportIds,
        ...(options.preview ? { preview: true } : {}),
      }),
    });
    return normalizeReportsTextFields(data);
  } catch (error: any) {
    throw new Error(`Failed to get reports: ${getErrorMessage(error)}`);
  }
};

/**
 * Get user's reports
 */
export const getUserReports = async (
  nickname: string
): Promise<ReportData[]> => {
  try {
    const data = await fetchWithErrorHandling(
      `${API_URL}/reports/user/${nickname}`
    );
    return normalizeReportsTextFields(data);
  } catch (error: any) {
    throw new Error(`Failed to get user reports: ${getErrorMessage(error)}`);
  }
};

/**
 * Perform incremental sync to get only changed reports
 */
export const performIncrementalSync = async (
  lastSync: string
): Promise<SyncResponse> => {
  try {
    const response = await syncData({ lastSync });
    return response;
  } catch (error) {
    console.error("Incremental sync failed:", error);
    throw error;
  }
};

/**
 * Sync only user data from server
 * Lightweight endpoint for checking user updates
 */
export const syncUserData = async (
  authToken: string
): Promise<{
  user?: UserContextData;
  serverTime: string;
}> => {
  try {
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    console.log("🔄 API CALL: /sync/user");
    const data = await fetchWithErrorHandling(`${API_URL}/sync/user`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });
    return {
      ...data,
      user: data.user ? normalizeUserDataTextFields(data.user) : data.user,
    };
  } catch (error: any) {
    throw new Error(`Failed to sync user data: ${getErrorMessage(error)}`);
  }
};
